import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Section, ghostButton, inputClass } from "@/components/settings/primitives";
import { ChangePasswordForm } from "@/components/settings/ChangePasswordForm";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

function DeleteAccount() {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const confirm = async () => {
    setBusy(true);
    try {
      await api.del("/account", { confirmation: "DELETE" });
      await signOut();
      toast.success("Your account and all its data were deleted");
      navigate("/sign-in", { replace: true });
    } catch (err) {
      toast.error(err.message || "Couldn't delete your account");
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="inline-flex h-10 items-center rounded-full border border-rose-400/30 px-5 text-sm text-rose-300 transition-colors hover:bg-rose-500/10" data-testid="settings-delete-account-button">
        Delete account
      </button>
      <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setTyped(""); }}>
        <AlertDialogContent className="border-white/10 bg-ink-850" data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete your account permanently?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This removes your profile, all conversations, messages, settings and usage history. It can't be undone. Type DELETE to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE" className={inputClass} data-testid="delete-account-confirm-input" />
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 bg-transparent text-zinc-300 hover:bg-white/[0.06] hover:text-white" data-testid="delete-account-cancel">Cancel</AlertDialogCancel>
            <button type="button" disabled={typed !== "DELETE" || busy} onClick={confirm} className="inline-flex h-10 items-center justify-center rounded-md bg-rose-500 px-4 text-sm font-medium text-white disabled:opacity-40" data-testid="delete-account-confirm-button">
              {busy ? "Deleting…" : "Delete account"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function SecuritySection({ me }) {
  const [busy, setBusy] = useState(false);
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const signOutAll = async () => {
    setBusy(true);
    try {
      await api.post("/auth/sign-out-all");
      await signOut();
      toast.success("Signed out of all devices");
      navigate("/sign-in", { replace: true });
    } catch (err) {
      toast.error(err.message || "Couldn't sign out everywhere");
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Section title="Sessions" description="Sign out of ChatSleek everywhere, including this device." testId="settings-sessions-section">
        <button type="button" onClick={signOutAll} disabled={busy} className={ghostButton} data-testid="settings-sign-out-all-button">
          {busy ? "Signing out…" : "Sign out of all devices"}
        </button>
      </Section>
      <Section title="Change password" testId="settings-security-password-section">
        <ChangePasswordForm email={me.email} testIdPrefix="security-password" />
      </Section>
      <Section title="Delete account" description="Permanently delete your ChatSleek account and all of its data." testId="settings-delete-account-section">
        <DeleteAccount />
      </Section>
    </div>
  );
}
